import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataGrid, { Column, Paging, FilterRow, Export, Button as GridButton } from "devextreme-react/data-grid";
import CheckBox from 'devextreme-react/check-box';
import TextArea from 'devextreme-react/text-area';
import { Popup } from 'devextreme-react/popup';
import { Form, SimpleItem, GroupItem, EmptyItem, Label, RequiredRule } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { LoadIndicator } from "devextreme-react/load-indicator";
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config'; // [수정] 중앙 설정 파일 import

// [수정] 모든 API 주소를 중앙 API_URL을 기반으로 정의합니다.
const EQUIP_API_URL = `${API_URL}/api/oracle/equip_use_req`;
const CUST_API_URL = `${API_URL}/api/oracle/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/oracle/common/devices`;
const TESTER_GROUP_API_URL = `${API_URL}/api/oracle/common/tester_groups`;
const TESTER_ID_API_URL = `${API_URL}/api/oracle/common/tester_ids`;

const PURPOSE_OPTIONS = [
  "ES 대응", "특성평가", "TEST 건설", "신뢰성 평가", "TEST SETUP", "불량분석", "부품인정"
];

const formatDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear().toString();
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}${h}${min}${s}`;
};

const parseApiDate = (dateString) => {
  if (!dateString || dateString.length < 14) return null;
  const year = dateString.substring(0, 4);
  const month = parseInt(dateString.substring(4, 6), 10) - 1;
  const day = dateString.substring(6, 8);
  const hour = dateString.substring(8, 10);
  const minute = dateString.substring(10, 12);
  const second = dateString.substring(12, 14);
  return new Date(year, month, day, hour, minute, second);
};

export default function EQUIP_USE_REQ({ user }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [custList, setCustList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);

  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dateTo, setDateTo] = useState(today);

  const [isPopupVisible, setPopupVisible] = useState(false);
  const [currentRequest, setCurrentRequest] = useState({});

  const [deviceList, setDeviceList] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [testerGroupList, setTesterGroupList] = useState([]);
  const [testerIdList, setTesterIdList] = useState([]);
  const [testerIdLoading, setTesterIdLoading] = useState(false);

  const renderConversionYN = (cellData) => {
    return (
      <CheckBox
        readOnly={true}
        value={cellData.value === 'Y'}
      />
    );
  };

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 고객사를 먼저 선택해주세요.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    axios.get(EQUIP_API_URL, {
      params: {
        factory: selectedFactory,
        inCustCd: selectedCust,
        inDateFrom: formatDate(dateFrom).substring(0, 8) + '000000',
        inDateTo: formatDate(dateTo).substring(0, 8) + '235959',
      }
    })
    .then(res => {
      if (res.data.retCode === 0) {
        const parsedData = (res.data.data || []).map(item => ({
          ...item,
          START_TIME: parseApiDate(item.START_TIME),
          END_TIME: parseApiDate(item.END_TIME),
          CREATE_TIME: parseApiDate(item.CREATE_TIME),
          UPDATE_TIME: parseApiDate(item.UPDATE_TIME),
        }));
        setData(parsedData);
      } else {
        throw new Error(res.data.retMsg);
      }
    })
    .catch(e => {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류";
      setError(`조회 오류: ${errorMessage}`);
    })
    .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, dateFrom, dateTo]);

  useEffect(() => {
    if (!selectedFactory) {
      setCustList([]);
      return;
    }
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => {
        if (res.data.retCode === 0) setCustList(res.data.data || []);
      })
      .catch(e => { console.error("CUST 로드 오류:", e); setCustList([]); });
  }, [selectedFactory]);

  useEffect(() => {
    if (user?.role !== "CUST") setSelectedCust(null);
  }, [selectedFactory, user?.role]);

  useEffect(() => {
    if (!selectedFactory) {
      setTesterGroupList([]);
      return;
    }
    axios.get(TESTER_GROUP_API_URL, { params: { factory: selectedFactory } })
      .then(res => {
        if (res.data.retCode === 0) setTesterGroupList(res.data.data || []);
      })
      .catch(e => { console.error("설비군 로드 오류:", e); setTesterGroupList([]); });
  }, [selectedFactory]);

  useEffect(() => {
    if (!isPopupVisible || !selectedFactory || !selectedCust) {
      setDeviceList([]);
      return;
    }
    setDeviceLoading(true);
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        if (res.data.retCode === 0) setDeviceList(res.data.data || []);
      })
      .catch(() => notify("Device 목록 로드 오류", "error", 2000))
      .finally(() => setDeviceLoading(false));
  }, [isPopupVisible, selectedFactory, selectedCust]);

  useEffect(() => {
    if (!isPopupVisible || !selectedFactory || !currentRequest.RES_TYPE) {
      setTesterIdList([]);
      return;
    }
    setTesterIdLoading(true);
    axios.get(TESTER_ID_API_URL, { params: { factory: selectedFactory, TESTER: currentRequest.RES_TYPE } })
      .then(res => {
        if (res.data.retCode === 0) setTesterIdList(res.data.data || []);
      })
      .catch(() => notify("설비호기 목록 로드 오류", "error", 2000))
      .finally(() => setTesterIdLoading(false));
  }, [isPopupVisible, selectedFactory, currentRequest.RES_TYPE]);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      setSelectedCust(found?.CODE_CD || null);
    }
  }, [custList, user]);

    const onExporting = (e) => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("장비사용_신청");
      exportDataGrid({
          component: e.component,
          worksheet: worksheet,
          autoFilterEnabled: true
      }).then(() => {
          workbook.xlsx.writeBuffer().then(buffer => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), "장비사용_신청.xlsx");
          });
      });
      e.cancel = true;
    };

  const handleNewClick = () => {
    setCurrentRequest({
      FACTORY: selectedFactory,
      CUSTOMER_ID: selectedCust,
      USER_ID: user.username,
      CONVERSION_YN: false,
      APP_STATUS: 'R',
      DEVICE: null,
      RES_TYPE: null,
      RES_ID: null,
      APP_HISTORY: "",
      PURPOSE: "",
      APP_COMMENT: "",
    });
    setPopupVisible(true);
  };

  const handleEditClick = (e) => {
    const dataToEdit = {
      APP_HISTORY: "",
      APP_COMMENT: "",
      ...e.row.data,
      CONVERSION_YN: e.row.data.CONVERSION_YN === 'Y',
    };
    setCurrentRequest(dataToEdit);
    setPopupVisible(true);
  };

  const handleDeleteClick = (e) => {
    confirm("<i>해당 요청을 삭제하시겠습니까?</i>", "삭제 확인").then((confirmed) => {
      if (confirmed) {
        setLoading(true);
        axios.delete(`${EQUIP_API_URL}/${e.row.data.KEY_TIME}`)
          .then(res => {
            if (res.data.retCode === 0) {
              notify("요청이 삭제되었습니다.", "success", 2000);
              fetchData();
            } else { throw new Error(res.data.retMsg); }
          })
          .catch(err => notify(`삭제 오류: ${err.message}`, "error", 3000))
          .finally(() => setLoading(false));
      }
    });
  };

  const handleSave = () => {
    setLoading(true);
    const payload = {
      ...currentRequest,
      START_TIME: formatDate(currentRequest.START_TIME),
      END_TIME: formatDate(currentRequest.END_TIME),
      CONVERSION_YN: currentRequest.CONVERSION_YN ? 'Y' : 'N',
    };
    const isNew = !payload.KEY_TIME;
    const promise = isNew 
      ? axios.post(EQUIP_API_URL, payload)
      : axios.put(`${EQUIP_API_URL}/${payload.KEY_TIME}`, payload);
      
    promise
      .then(res => {
        if (res.data.retCode === 0) {
          notify(isNew ? "등록되었습니다." : "수정되었습니다.", "success", 2000);
          setPopupVisible(false);
          fetchData();
        } else {
          throw new Error(res.data.retMsg);
        }
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류";
        notify(`저장 오류: ${errorMessage}`, "error", 3000);
      })
      .finally(() => setLoading(false));
  };

  const onTesterGroupChanged = (e) => {
    setCurrentRequest(prev => ({ ...prev, RES_TYPE: e.value, RES_ID: null }));
  };

  return (
    <div style={{ padding: 20, fontFamily: 'Arial, sans-serif' }}>
      <h3 style={{ marginBottom: 20 }}>Customer Equipment Usage Request</h3>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 15, marginBottom: 20, alignItems: "center" }}>
        <SelectBox 
          items={['FT01', 'PT01']} 
          value={selectedFactory} 
          onValueChanged={(e) => setSelectedFactory(e.value)} 
          label="Factory" 
          width={120} 
          labelMode="floating"
        />
        <SelectBox 
          dataSource={custList} 
          value={selectedCust} 
          valueExpr="CODE_CD" 
          displayExpr="CODE_NM" 
          onValueChanged={e => setSelectedCust(e.value)} 
          disabled={user?.role === "CUST"} 
          searchEnabled 
          showClearButton={user?.role !== "CUST"} 
          placeholder="Select CUST" 
          label="CUST" 
          noDataText="선택된 Factory에 해당하는 고객사가 없습니다." 
          labelMode="floating"
          width={220}
        />
        <DateBox 
          value={dateFrom} 
          displayFormat="yyyy-MM-dd" 
          type="date" 
          onValueChanged={e => setDateFrom(e.value)} 
          label="Start Date(From)" 
          labelMode="floating"
          width={200}
        />
        <DateBox 
          value={dateTo} 
          displayFormat="yyyy-MM-dd" 
          type="date" 
          onValueChanged={e => setDateTo(e.value)} 
          label="Start Date(To)" 
          labelMode="floating"
          width={200}
        />
        <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        <Button text="New Request" type="success" icon="add" onClick={handleNewClick} disabled={!selectedCust} />
      </div>

      {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      {loading && <LoadIndicator visible={true} />}

      <DataGrid
          dataSource={data}
          keyExpr="KEY_TIME"
          showBorders={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          hoverStateEnabled={true}
          style={{ height: 'calc(100vh - 250px)', minHeight: 400 }}
          onExporting={onExporting}
      >
        <Export enabled={true} allowExportSelectedData={false} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />

          <Column type="buttons" width={110}>
            <GridButton name="edit" icon="edit" onClick={handleEditClick} visible={true} />
            <GridButton name="delete" icon="trash" onClick={handleDeleteClick} visible={true} />
          </Column>

          <Column dataField="APP_STATUS" caption="상태" />
          <Column dataField="DEVICE" caption="DEVICE" />
          <Column dataField="LOT_ID" caption="LOT ID" />
          <Column dataField="START_TIME" caption="시작 시간" dataType="datetime" format="yyyy-MM-dd HH:mm" />
          <Column dataField="END_TIME" caption="종료 시간" dataType="datetime" format="yyyy-MM-dd HH:mm" />
          <Column dataField="USE_HOUR" caption="사용시간" />
          <Column dataField="PURPOSE" caption="사용 목적" />
          <Column dataField="RES_TYPE" caption="설비군" />
          <Column dataField="RES_ID" caption="설비호기" />
                   
          <Column 
            dataField="CONVERSION_YN" 
            caption="개조" 
            alignment="center"
            cellRender={renderConversionYN}
          />

          <Column dataField="USER_ID" caption="신청자" />
          <Column dataField="APP_HISTORY" caption="사용 내용" />
          <Column dataField="APP_COMMENT" caption="신청 사유" />
          <Column dataField="UPDATE_TIME" caption="최종수정일" dataType="datetime" format="yyyy-MM-dd HH:mm" />
      </DataGrid>

        <Popup
      visible={isPopupVisible}
      onHiding={() => setPopupVisible(false)}
      dragEnabled={true}
      showCloseButton={true}
      showTitle={true}
      title={currentRequest.KEY_TIME ? "장비 사용 신청 수정" : "장비 사용 신규 신청"}
      width={800}
      height="auto"
      maxHeight={1000}
      wrapperAttr={{ style: { padding: 20 } }}
    >
      <Form
        formData={currentRequest}
        labelLocation="top"
        onFieldDataChanged={({ dataField, value }) => {
          const updated = { ...currentRequest, [dataField]: value };

          if (dataField === "START_TIME" || dataField === "END_TIME") {
            const start = new Date(updated.START_TIME);
            const end = new Date(updated.END_TIME);
            if (!isNaN(start) && !isNaN(end) && end > start) {
              const diffMs = end - start;
              const diffHours = diffMs / (1000 * 60 * 60);
              updated.USE_HOUR = parseFloat(diffHours.toFixed(2));
            } else {
              updated.USE_HOUR = null;
            }
          }

          setCurrentRequest(updated);
        }}
      >
        <GroupItem colCount={2} colCountByScreen={{ xs: 1, sm: 2 }}>
          <SimpleItem
            dataField="DEVICE"
            editorType="dxSelectBox"
            editorOptions={{
              dataSource: deviceList,
              valueExpr: 'CODE_CD',
              displayExpr: 'CODE_CD',
              searchEnabled: true,
              disabled: deviceLoading,
              noDataText: deviceLoading ? '불러오는 중...' : '해당 조건의 Device가 없습니다.'
            }}
          >
            <Label text="Device" />
            <RequiredRule message="Device는 필수입니다." />
          </SimpleItem>

          <SimpleItem dataField="LOT_ID" editorType="dxTextBox">
            <Label text="Lot ID" />
          </SimpleItem>

          <SimpleItem dataField="START_TIME" editorType="dxDateBox" editorOptions={{
            type: 'datetime',
            displayFormat: 'yyyy-MM-dd HH:mm'
          }}>
            <Label text="사용 시작 시간" />
            <RequiredRule message="시작 시간은 필수입니다." />
          </SimpleItem>

          <SimpleItem dataField="END_TIME" editorType="dxDateBox" editorOptions={{
            type: 'datetime',
            displayFormat: 'yyyy-MM-dd HH:mm'
          }}>
            <Label text="사용 종료 시간" />
            <RequiredRule message="종료 시간은 필수입니다." />
          </SimpleItem>

          <SimpleItem dataField="USE_HOUR" editorType="dxNumberBox" editorOptions={{
            showSpinButtons: true,
            readOnly: true
          }}>
            <Label text="총 사용 시간" />
            <RequiredRule message="총 사용 시간은 필수입니다." />
          </SimpleItem>

          <EmptyItem />

          <SimpleItem dataField="RES_TYPE" editorType="dxSelectBox" editorOptions={{
            dataSource: testerGroupList,
            valueExpr: 'TESTER',
            displayExpr: 'TESTER',
            onValueChanged: onTesterGroupChanged,
            placeholder: '설비군을 선택하세요',
            searchEnabled: true
          }}>
            <Label text="자원 타입 (설비군)" />
            <RequiredRule message="설비군은 필수입니다." />
          </SimpleItem>

          <SimpleItem dataField="RES_ID" editorType="dxSelectBox" editorOptions={{
            dataSource: testerIdList,
            valueExpr: 'TESTER_ID',
            displayExpr: 'TESTER_ID',
            placeholder: '설비호를 선택하세요',
            searchEnabled: true,
            disabled: !currentRequest.RES_TYPE || testerIdLoading,
            noDataText: testerIdLoading ? '불러오는 중...' : '해당 설비군에 속한 설비가 없습니다.'
          }}>
            <Label text="자원 ID (설비호기)" />
            <RequiredRule message="설비호기는 필수입니다." />
          </SimpleItem>

          <SimpleItem
            dataField="CONVERSION_YN"
            editorType="dxCheckBox"
            editorOptions={{ text: "Conversion Request" }}
          >
            <Label visible={false} />
          </SimpleItem>

          <EmptyItem />

          <SimpleItem dataField="PURPOSE" editorType="dxSelectBox" colSpan={2} editorOptions={{
            items: PURPOSE_OPTIONS,
            placeholder: "목적 선택",
            searchEnabled: true
          }}>
            <Label text="사용 목적" />
            <RequiredRule message="사용 목적은 필수입니다." />
          </SimpleItem>

          <GroupItem colCount={2} colSpan={2}>
            <SimpleItem
              dataField="APP_HISTORY"
              editorComponent={TextArea}
              editorOptions={{
                height: 100,
                maxLength: 300,
                placeholder: "신청 내용을 입력하세요 (최대 300자)"
              }}
            >
              <Label text="사용 내용 (신청 내용)" />
            </SimpleItem>

            <SimpleItem
              dataField="APP_COMMENT"
              editorComponent={TextArea}
              editorOptions={{
                height: 100,
                placeholder: "신청 사유를 입력하세요 (선택 사항)"
              }}
            >
              <Label text="신청 사유 (Comment)" />
            </SimpleItem>
          </GroupItem>
        </GroupItem>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 25, gap: 12 }}>
        <Button text="저장" type="success" onClick={handleSave} disabled={loading} />
        <Button text="취소" type="normal" onClick={() => setPopupVisible(false)} />
      </div>
    </Popup>

    </div>
  );
}